if (!customElements.get('kiwi-click-video-tabs')) {
  class KiwiClickVideoTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.items = Array.from(this.querySelectorAll('[data-kiwi-video-tab]'));
      this.slides = Array.from(this.querySelectorAll('[data-kiwi-video-slide]'));
      const initialIndex = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.activeIndex = -1;
      this.initialized = true;

      this.handleItemClick = this.handleItemClick.bind(this);
      this.handleItemKeydown = this.handleItemKeydown.bind(this);
      this.handleMediaClick = this.handleMediaClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);

      this.items.forEach((item) => item.addEventListener('click', this.handleItemClick));
      this.items.forEach((item) => item.addEventListener('keydown', this.handleItemKeydown));
      this.slides.forEach((slide) => slide.addEventListener('click', this.handleMediaClick));
      this.bindVideoState();
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.goTo(initialIndex);
    }

    disconnectedCallback() {
      this.items.forEach((item) => item.removeEventListener('click', this.handleItemClick));
      this.items.forEach((item) => item.removeEventListener('keydown', this.handleItemKeydown));
      this.slides.forEach((slide) => slide.removeEventListener('click', this.handleMediaClick));
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }

    bindVideoState() {
      this.slides.forEach((slide) => {
        const video = slide.querySelector('[data-kiwi-click-video]');
        if (!video) return;

        video.addEventListener('play', () => slide.classList.add('is-playing'));
        video.addEventListener('pause', () => slide.classList.remove('is-playing'));
        video.addEventListener('ended', () => slide.classList.remove('is-playing'));
      });
    }

    handleItemClick(event) {
      const index = Number.parseInt(event.currentTarget.dataset.index, 10);
      if (Number.isNaN(index)) return;
      this.goTo(index);
    }

    handleItemKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      const index = Number.parseInt(event.currentTarget.dataset.index, 10);
      if (Number.isNaN(index)) return;
      this.goTo(index);
    }

    handleMediaClick(event) {
      const slide = event.currentTarget;
      if (!slide.classList.contains('is-active')) return;

      const video = slide.querySelector('[data-kiwi-click-video]');
      if (!video) return;

      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }

    handleBlockSelect(event) {
      const item = event.target?.closest?.('[data-kiwi-video-tab]');
      if (!item || !this.contains(item)) return;

      const index = Number.parseInt(item.dataset.index, 10);
      if (Number.isNaN(index)) return;
      this.goTo(index);
    }

    pauseInactiveVideos(activeIndex) {
      this.slides.forEach((slide) => {
        const slideIndex = Number.parseInt(slide.dataset.index, 10);
        if (slideIndex === activeIndex) return;

        const video = slide.querySelector('[data-kiwi-click-video]');
        if (!video) return;

        video.pause();
        video.currentTime = 0;
        slide.classList.remove('is-playing');
      });
    }

    playActiveVideos(activeIndex) {
      this.slides.forEach((slide) => {
        const slideIndex = Number.parseInt(slide.dataset.index, 10);
        if (slideIndex !== activeIndex || !slide.classList.contains('is-active')) return;

        const video = slide.querySelector('[data-kiwi-click-video]');
        if (!video) return;

        video.muted = true;
        video.play().catch(() => {});
      });
    }

    goTo(index) {
      if (!this.items.length || !this.slides.length) return;

      const normalizedIndex = ((index % this.items.length) + this.items.length) % this.items.length;
      if (normalizedIndex === this.activeIndex) return;
      this.activeIndex = normalizedIndex;
      this.pauseInactiveVideos(normalizedIndex);

      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === normalizedIndex;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.tabIndex = isActive ? 0 : -1;
      });

      this.slides.forEach((slide) => {
        const slideIndex = Number.parseInt(slide.dataset.index, 10);
        const isActive = slideIndex === normalizedIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.playActiveVideos(normalizedIndex);
    }
  }

  customElements.define('kiwi-click-video-tabs', KiwiClickVideoTabs);
}
